// MF-554 — Generic renderer for Library items of kind 'chart'. The backend sends normalised data
// ({ rows, encoding: { x, series[] }, meta.presentation }); this decides how it looks. One y-axis
// only; hidden series come from the tile overrides; the table view is always available.
import { useEffect, useMemo, useRef } from 'react'
import ReactECharts from 'echarts-for-react'
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { FONT, INK, TONE_COLORS, baseTextStyle, legendBase, tooltipBase, fmtValue } from './boardTheme'
import { resolveColors, useBoardStyle } from './boardPalettes'

const unitOf = (s) => (s?.unit ? s.unit : '')

const DataTable = ({ rows, x, series }) => (
  <Box sx={{ overflow: 'auto', height: '100%' }}>
    <Table size='small' stickyHeader>
      <TableHead>
        <TableRow>
          {x ? <TableCell sx={{ fontWeight: 600 }}>{x}</TableCell> : null}
          {series.map((s) => (
            <TableCell key={s.key} align='right' sx={{ fontWeight: 600 }}>
              {s.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i}>
            {x ? <TableCell>{String(r[x] ?? '–')}</TableCell> : null}
            {series.map((s) => (
              <TableCell key={s.key} align='right'>
                {fmtValue(r[s.key], Number.isInteger(r[s.key]) ? 0 : 1)}
                {unitOf(s)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
)

// order: { by: 'source' | 'value' | 'label', dir: 'asc' | 'desc' } — a tile property. 'source' keeps the chart's own order.
const orderRows = (rows, x, valueKey, order) => {
  if (!order || order.by === 'source' || !Array.isArray(rows)) return rows
  const key = order.by === 'label' ? x : valueKey
  if (!key) return rows
  const sign = order.dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const va = a[key]
    const vb = b[key]
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sign
    return String(va ?? '').localeCompare(String(vb ?? ''), undefined, { numeric: true }) * sign
  })
}

// legend: 'auto' (only with 2+ series) | 'show' | 'hide' — a tile override. The legend is live: a
// click on an entry hides / shows that series in the chart.
const legendShown = (legend, count) => (legend === 'show' ? true : legend === 'hide' ? false : count > 1)

const buildOption = (type, rows, x, series, yMax, legend = 'auto', toneKey = null, extra = {}) => {
  const { legendPosition = 'top', range = {}, colors = [], stack = false } = extra
  const palette = colors.length ? colors : ['#2A69A7']
  const cats = rows.map((r) => String(r[x] ?? ''))
  const showLegend = legendShown(legend, series.length)
  const tooltip = {
    ...tooltipBase,
    trigger: type === 'donut' ? 'item' : 'axis',
    axisPointer: { type: type === 'line' || type === 'area' ? 'line' : 'shadow' },
    valueFormatter: (v) => `${fmtValue(v, Number.isInteger(v) ? 0 : 1)}${unitOf(series[0])}`
  }
  if (type === 'donut') {
    const s = series[0]
    return {
      textStyle: baseTextStyle,
      tooltip,
      legend:
        legend === 'hide'
          ? { show: false }
          : {
              ...legendBase,
              type: 'scroll',
              ...(legendPosition === 'bottom' ? { top: 'auto', bottom: 0 } : {})
            },
      color: palette,
      series: [
        {
          type: 'pie',
          radius: ['52%', '78%'],
          top: legend === 'hide' || legendPosition === 'bottom' ? 0 : 24,
          bottom: legend !== 'hide' && legendPosition === 'bottom' ? 24 : 0,
          itemStyle: { borderColor: '#fff', borderWidth: 2 },
          label: { fontFamily: FONT, color: INK.secondary, fontSize: 11, formatter: '{b}' },
          data: rows.map((r) => ({ name: String(r[x] ?? ''), value: r[s.key] }))
        }
      ]
    }
  }
  const horizontal = type === 'hbar'
  const catAxis = {
    type: 'category',
    data: cats,
    axisLine: { lineStyle: { color: INK.axis } },
    axisTick: { show: false },
    axisLabel: {
      color: INK.secondary,
      fontSize: 11,
      width: horizontal ? 140 : undefined,
      overflow: 'truncate'
    }
  }
  const valAxis = {
    type: 'value',
    min: range.min ?? undefined,
    max: range.max ?? (yMax || undefined),
    splitLine: { lineStyle: { color: INK.grid } },
    axisLabel: { color: INK.muted, fontSize: 11, hideOverlap: true }
  }
  return {
    textStyle: baseTextStyle,
    animationDuration: 400,
    tooltip,
    legend: showLegend
      ? {
          ...legendBase,
          data: series.map((s) => s.label),
          ...(legendPosition === 'bottom' ? { top: 'auto', bottom: 0 } : {})
        }
      : undefined,
    // hbar: room on the right for the value label of the longest bar
    grid: {
      top: showLegend && legendPosition !== 'bottom' ? 32 : 12,
      left: 8,
      right: horizontal ? 40 : 16,
      bottom: showLegend && legendPosition === 'bottom' ? 36 : 12,
      containLabel: true
    },
    xAxis: horizontal ? valAxis : catAxis,
    yAxis: horizontal ? { ...catAxis, inverse: true } : valAxis,
    series: series.map((s, i) => ({
      name: s.label,
      type: type === 'area' ? 'line' : type === 'hbar' ? 'bar' : type,
      // toneKey: each bar takes the colour of what it means (blue good / orange attention / grey other)
      data:
        toneKey && (type === 'bar' || type === 'hbar')
          ? rows.map((r) => ({
              value: r[s.key],
              itemStyle: { color: TONE_COLORS[r[toneKey]] || TONE_COLORS.neutral }
            }))
          : rows.map((r) => r[s.key]),
      color: palette[i % palette.length],
      // MF-568 — parts of a whole stacked in one bar (e.g. with / without PAT data)
      stack: stack && (type === 'bar' || type === 'hbar') ? 'total' : undefined,
      barMaxWidth: 36,
      itemStyle:
        type === 'bar' || type === 'hbar'
          ? { borderRadius: horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0] }
          : undefined,
      smooth: false,
      lineStyle: { width: 2 },
      symbolSize: 8,
      areaStyle: type === 'area' ? { opacity: 0.12 } : undefined,
      // Direct labels only when there are few points (never a number on every bar of a long axis).
      label:
        rows.length <= 8 && series.length === 1
          ? {
              show: true,
              position: horizontal ? 'right' : 'top',
              fontSize: 11,
              color: INK.secondary,
              formatter: ({ value }) => fmtValue(value, Number.isInteger(value) ? 0 : 1)
            }
          : undefined
    }))
  }
}

/**
 * @param {{ data: { rows, encoding, meta }, overrides?: { type?, hiddenSeries?, legend? }, tableView?: boolean }} props
 */
const ChartRenderer = ({ data, overrides = {}, tableView = false }) => {
  const style = useBoardStyle()
  const sourceRows = useMemo(() => data?.rows || [], [data])
  const presentation = data?.meta?.presentation || {}
  const x = data?.encoding?.x || presentation.x
  const hiddenKey = (overrides.hiddenSeries || []).join('|')
  const allSeries = data?.encoding?.series
  const series = useMemo(
    () => (allSeries || []).filter((s) => !(overrides.hiddenSeries || []).includes(s.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allSeries, hiddenKey]
  )
  const type = (presentation.types || []).includes(overrides.type)
    ? overrides.type
    : presentation.defaultType || 'bar'
  // MF-574 — the order of the data is a property of the tile (the chart's own order when not set)
  const orderKey = JSON.stringify(overrides.order || null)
  const rows = useMemo(
    () => orderRows(sourceRows, x, series[0]?.key, overrides.order),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sourceRows, x, series, orderKey]
  )
  const colorsKey = JSON.stringify(overrides.colors || null)
  const colors = useMemo(
    () => resolveColors(style, overrides.colors, type === 'donut' ? rows.length : series.length),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [style, colorsKey, series.length, rows.length, type]
  )
  const rangeKey = JSON.stringify(overrides.range || null)

  // ECharts measures its box once; inside a resizable grid tile it must follow the box (MF-554 F4).
  const chartRef = useRef(null)
  const boxRef = useRef(null)
  useEffect(() => {
    const el = boxRef.current
    if (!el) return undefined
    const ro = new ResizeObserver(() => chartRef.current?.getEchartsInstance?.()?.resize())
    ro.observe(el)
    return () => ro.disconnect()
  }, [tableView, overrides.type])

  const option = useMemo(
    () =>
      buildOption(type, rows, x, series, presentation.yMax, overrides.legend, presentation.toneKey, {
        legendPosition: overrides.legendPosition,
        range: overrides.range || {},
        colors,
        stack: presentation.stack
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      type,
      rows,
      x,
      series,
      presentation.yMax,
      overrides.legend,
      overrides.legendPosition,
      rangeKey,
      presentation.toneKey,
      presentation.stack,
      colors
    ]
  )

  if (tableView || type === 'table') return <DataTable rows={rows} x={x} series={series} />
  return (
    <div ref={boxRef} style={{ height: '100%', width: '100%', minWidth: 0, overflow: 'hidden' }}>
      <ReactECharts ref={chartRef} option={option} notMerge style={{ height: '100%', width: '100%' }} />
    </div>
  )
}

export default ChartRenderer
